import { cva } from "class-variance-authority";
import cn from "classnames";
import { motion } from "framer-motion";

export type ExpandButtonProps = {
  parentSurfaceClass: 'surface-l0' | 'surface-l1' | 'surface-l2'
  size: 'small' | 'large'
  onClick: () => void;
  isExpanded: boolean;
};

const buttonVariants = cva(
  'transition-all duration-800 ease-in-out rounded-full icon-action hover:icon-action-hover',
  {
    variants: {
      parentSurfaceClass: {
        'surface-l0': 'surface-l1 hover:surface-l1-active',
        'surface-l1': 'surface-l2 hover:surface-l2-active',
        'surface-l2': 'surface-l3 hover:surface-l3-active',
      },
      size: {
        'small': 'p-1',
        'large': 'p-xs'
      }
    },
    defaultVariants: {
      parentSurfaceClass: 'surface-l0',
      size: 'small',
    },
  }
);

const AnimatedIconVariants = {
  initial: {
    rotate: 0,
  },
  animate: {
    rotate: 90,
  },
};

const AnimatedIconTransition = {
  duration: 0.4,
};

const SmallExpandButtonIcon = ({ isExpanded }: { isExpanded: boolean }) => {
  return (
    <motion.svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
      <motion.path d="M5.75 2.25C5.75 1.97386 5.97386 1.75 6.25 1.75C6.52614 1.75 6.75 1.97386 6.75 2.25L6.75 9.25C6.75 9.52614 6.52614 9.75 6.25 9.75C5.97386 9.75 5.75 9.52614 5.75 9.25L5.75 2.25Z" fill="currentColor"
        initial="initial"
        animate={isExpanded ? "animate" : "initial"}
        variants={AnimatedIconVariants}
        transition={AnimatedIconTransition} />
      <path d="M2.75 6.24994C2.47386 6.24994 2.25 6.02608 2.25 5.74994C2.25 5.4738 2.47386 5.24994 2.75 5.24994L9.75 5.24994C10.0261 5.24994 10.25 5.4738 10.25 5.74994C10.25 6.02608 10.0261 6.24994 9.75 6.24994L2.75 6.24994Z" fill="currentColor" />
    </motion.svg>
  );
};

const LargeExpandButtonIcon = ({ isExpanded }: { isExpanded: boolean }) => {
  return (
    <motion.svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <motion.path d="M7.6665 3C7.6665 2.63181 7.96498 2.33333 8.33317 2.33333C8.70136 2.33333 8.99984 2.63181 8.99984 3L8.99984 12.3333C8.99984 12.7015 8.70136 13 8.33317 13C7.96498 13 7.6665 12.7015 7.6665 12.3333L7.6665 3Z" fill="currentColor"
        initial="initial"
        animate={isExpanded ? "animate" : "initial"}
        variants={AnimatedIconVariants}
        transition={AnimatedIconTransition} />
      <path d="M3.6665 8.33325C3.2832 8.33325 2.99984 8.03477 2.99984 7.66658C2.99984 7.29839 3.29832 6.99992 3.6665 6.99992L12.9998 6.99992C13.368 6.99992 13.6665 7.2984 13.6665 7.66659C13.6665 8.03478 13.368 8.33325 12.9998 8.33325L3.6665 8.33325Z" fill="currentColor" />
    </motion.svg>
  );
};

export const ExpandButton = ({ parentSurfaceClass, size, onClick, isExpanded = false }: ExpandButtonProps) => {
  return (
    <button className={cn(buttonVariants({ parentSurfaceClass, size }))} onClick={onClick}>
      {size === 'small' ? <SmallExpandButtonIcon isExpanded={isExpanded} /> : <LargeExpandButtonIcon isExpanded={isExpanded} />}
    </button>
  );
};
